import { useQueryClient } from "@tanstack/react-query";
import React, { useState } from "react";

import { faEllipsis, faPlus } from "@fortawesome/pro-regular-svg-icons";
import { faGlobe } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import {
	ButtonStandard,
	Chip,
	DropDownMenu,
	Icon,
	Pagination,
	Skeleton,
	Table,
	Text,
	Tooltip,
	toast,
} from "@performyard/py-components";
import type { PYColumnDef, SortChangedEvent } from "@performyard/py-components";

import { AdminTitle } from "../../../../components/Admin";
import FilterChips from "../../../../components/FilterChips";
import ArchiveModal from "./ArchiveModal";
import CompetenciesFilterPanel from "./CompetenciesFilterPanel";

import {
	competenciesKeys,
	useArchiveCompetency,
	useCompetencies,
	useReopenCompetency,
} from "../../../../api/hooks/defs/competencies";
import useNavigate from "../../../../hooks/useNavigate";

import type { Competency } from "../../../../api/services/defs/competencies";

import { convertFiltersToFilterChips, getCompetenciesListParams, getNonDismissibleChipIds } from "./utils";

import StandardToolbarActionRow from "../../../../pageTemplates/InfoPageTemplate/StandardToolbarActionRow";
import useInfoPageTemplateQueryState from "../../../../pageTemplates/InfoPageTemplate/useInfoPageTemplateQueryState";
import { COMPETENCIES_PAGE_SIZE, type CompetenciesFilters, DEFAULT_FILTERS } from "./schema";

const activeOptions = [
	{ key: "edit", label: "Edit" },
	{ key: "archive", label: "Archive" },
];

const archivedOptions = [{ key: "reopen", label: "Reopen" }];

// The py-components Table wrapper seeds `loading=true` with empty-object rows and relies on
// its default cellRendererSelector to swap in a Skeleton — that selector is bypassed when
// a column supplies its own cellRenderer, so each custom cellRenderer has to opt back in.
const isLoadingRow = (data: Competency | Record<string, never>) => !data || Object.keys(data).length === 0;

const CompetenciesTab = () => {
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const [archiveTarget, setArchiveTarget] = useState<Competency | null>(null);
	const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);

	const {
		filters: rawFilters,
		setFilters,
		sort,
		setSort,
		searchValue,
		debouncedSearchValue,
		setDebouncedSearch,
		pageNumber,
		setPageNumber,
	} = useInfoPageTemplateQueryState<CompetenciesFilters>({
		defaultSortName: "name",
		defaultSortDirection: "asc",
		defaultPageNumber: 1,
		fallbackFilters: DEFAULT_FILTERS,
	});

	// useInfoPageTemplateQueryState returns Partial<FilterType>; merge defaults so
	// status is always populated and the shape is a complete CompetenciesFilters.
	const filters: CompetenciesFilters = { ...DEFAULT_FILTERS, ...rawFilters };

	const listParams = getCompetenciesListParams(filters, debouncedSearchValue, sort);
	const { data, isLoading } = useCompetencies(
		{
			...listParams,
			limit: COMPETENCIES_PAGE_SIZE,
			skip: (pageNumber - 1) * COMPETENCIES_PAGE_SIZE,
		},
		{ placeholderData: (previousData) => previousData }
	);

	const competencies = data?.competencies ?? [];
	const totalCount = data?.totalCount ?? 0;
	const pageCount = Math.ceil(totalCount / COMPETENCIES_PAGE_SIZE);

	const chips = convertFiltersToFilterChips(filters);
	const nonDismissibleChipIds = getNonDismissibleChipIds(filters);

	const { mutate: archiveMutate } = useArchiveCompetency({
		onSuccess: () => {
			toast.success("Competency Archived");
			queryClient.invalidateQueries({ queryKey: competenciesKeys.all });
		},
		onError: (error) => {
			toast.error((error as Error).message);
		},
	});

	const { mutate: reopenMutate } = useReopenCompetency({
		onSuccess: () => {
			toast.success("Competency Reopened");
			queryClient.invalidateQueries({ queryKey: competenciesKeys.all });
		},
		onError: (error) => {
			toast.error((error as Error).message);
		},
	});

	const handleAction = (key: string, row: Competency) => {
		switch (key) {
			case "edit":
				navigate(`/administration/competencies/${row._id}/edit`);
				break;
			case "archive":
				setArchiveTarget(row);
				break;
			case "reopen":
				reopenMutate(row._id);
				break;
		}
	};

	const onSortChanged = (event: SortChangedEvent<Competency>) => {
		const columnState = event.api.getColumnState();
		const sortedColumn = columnState.find((col) => col.sort);
		if (sortedColumn?.colId) {
			setSort({
				name: sortedColumn.colId,
				direction: sortedColumn.sort as "asc" | "desc",
			});
		}
	};

	const handleDeleteFilter = (id: string) => {
		if (id === "status") {
			// Dismissing a non-default Status chip reverts to the default ("active"),
			// which is non-dismissible and matches the page's baseline state.
			setFilters({ ...filters, status: DEFAULT_FILTERS.status });
		} else if (id === "companyWide") {
			const { companyWide: _discarded, ...rest } = filters;
			setFilters(rest);
		}
	};

	const columns: PYColumnDef<Competency>[] = [
		{
			colId: "name",
			headerName: "Competency",
			flex: 1,
			sortable: true,
			cellRenderer: (params: { data: Competency }) => {
				const row = params.data;
				if (isLoadingRow(row)) return <Skeleton className="h-4 w-3/4" />;
				return (
					<div className="flex items-center gap-1.5">
						<Text size="paragraph-3" weight="regular">
							<span className="py-line-clamp-1">{row.name}</span>
						</Text>
						{row.is_company_wide && (
							<Tooltip content="Company Wide Competency">
								<span
									data-testid="company-wide-icon"
									className="flex size-4 shrink-0 items-center justify-center"
								>
									<FontAwesomeIcon icon={faGlobe} className="text-sm text-primary-5" />
								</span>
							</Tooltip>
						)}
					</div>
				);
			},
		},
		{
			colId: "status",
			headerName: "Status",
			sortable: true,
			width: 160,
			cellStyle: { display: "flex", alignItems: "center" },
			cellRenderer: (params: { data: Competency }) => {
				const row = params.data;
				if (isLoadingRow(row)) return <Skeleton className="h-5 w-14 rounded-full" />;
				return row.status === "active" ? (
					<Chip label="Active" color="primary" size={20} />
				) : (
					<Chip label="Archived" color="neutral" size={20} />
				);
			},
		},
		{
			colId: "levels",
			headerName: "Levels",
			sortable: true,
			width: 120,
			headerClass: "competencies-centered-col",
			cellStyle: { display: "flex", alignItems: "center", justifyContent: "center" },
			valueGetter: (params: { data?: Competency }) => params.data?.levels?.length ?? 0,
			cellRenderer: (params: { data: Competency }) => {
				const row = params.data;
				if (isLoadingRow(row)) return <Skeleton className="h-4 w-6" />;
				return row.levels?.length ?? 0;
			},
		},
		{
			colId: "actions",
			headerName: "",
			headerClass: "competencies-actions-col",
			cellClass: "competencies-actions-col",
			sortable: false,
			width: 50,
			resizable: false,
			suppressSizeToFit: true,
			cellStyle: { display: "flex", alignItems: "center", justifyContent: "center", paddingTop: "8px" },
			cellRenderer: (params: { data: Competency }) => {
				const row = params.data;
				if (isLoadingRow(row)) return null;
				return (
					<DropDownMenu
						options={row.status === "active" ? activeOptions : archivedOptions}
						onSelect={(key) => handleAction(key, row)}
						placement="bottom-end"
						minWidth={80}
					>
						<Icon icon={faEllipsis} size={24} color="neutral-5" />
					</DropDownMenu>
				);
			},
		},
	];

	return (
		<div>
			<style>{`
				.competencies-centered-col .ag-header-cell-label { justify-content: center; }
				.competencies-actions-col { padding-right: 4px !important; }
			`}</style>
			<AdminTitle includeSpacer={false}>Competencies</AdminTitle>

			<div className="mb-2 flex items-center justify-between gap-4">
				<div className="min-w-0 flex-1">
					<StandardToolbarActionRow
						searchInput={searchValue}
						onSearchInputChange={(value) => setDebouncedSearch(value)}
						searchPlaceholder="Search competencies..."
						onFilterButtonPress={() => setIsFilterPanelOpen(true)}
						activeFiltersCount={chips.length}
					/>
				</div>
				<div>
					<ButtonStandard
						color="primary"
						iconLeft={faPlus}
						onClick={() => navigate("/administration/competencies/create")}
					>
						Create Competency
					</ButtonStandard>
				</div>
			</div>

			<div className="mb-2">
				<FilterChips
					filters={chips}
					label="Filters applied: "
					nonDismissibleFilters={nonDismissibleChipIds}
					onDeleteFilter={handleDeleteFilter}
				/>
			</div>

			<div
				style={{
					// 44 = AG Grid header slot; 48 = row. Loading height = header + 3 skeleton rows (188px).
					// Empty state matches py-components Table's internal 356px so the built-in overlay sits
					// correctly and there's no wrapper/Table height mismatch.
					height: isLoading
						? `${44 + 3 * 48}px`
						: competencies.length === 0
							? "356px"
							: `${44 + competencies.length * 48}px`,
					transition: "height 150ms ease-out",
				}}
			>
				<Table<Competency>
					columnDefs={columns}
					rowData={competencies}
					loading={isLoading}
					getRowId={(params) => params.data._id}
					rowHeight={48}
					defaultColDef={{
						cellStyle: { display: "flex", alignItems: "center" },
						resizable: false,
					}}
					emptyStateText="No Competencies Found"
					onSortChanged={onSortChanged}
					initialState={{
						sort: {
							sortModel: sort.name ? [{ colId: sort.name, sort: sort.direction as "asc" | "desc" }] : [],
						},
					}}
				/>
			</div>

			{pageCount > 1 && (
				<div className="mt-4 flex justify-center">
					<Pagination currentPage={pageNumber} pageCount={pageCount} onPageChange={setPageNumber} />
				</div>
			)}

			<CompetenciesFilterPanel
				isOpen={isFilterPanelOpen}
				onClose={() => setIsFilterPanelOpen(false)}
				filters={filters}
				onApplyFilters={(newFilters) => setFilters(newFilters)}
			/>

			{archiveTarget && (
				<ArchiveModal
					isOpen
					competencyName={archiveTarget.name}
					onConfirm={() => {
						archiveMutate(archiveTarget._id);
						setArchiveTarget(null);
					}}
					onClose={() => setArchiveTarget(null)}
				/>
			)}
		</div>
	);
};

export default CompetenciesTab;
